import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import inventoryAPI from "../../api/inventory/inventoryApi";
import LocationSelect from "../../components/partials/inventory/common/LocationSelect";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import { useLoader } from "../../utils/LoaderUtils";
import { useSearchParams } from "react-router-dom";
import TableShimmer from "../../components/partials/TableShimmer";
import StockHistoryRow from "./StockHistoryRow";

const REASON_LABELS = {
  stock_received_no_invoice: "Stock Received",
  purchase_invoice: "Purchase Invoice",
  customer_return: "Customer Return",
  transfer_in: "Transfer In",
  transfer_out: "Transfer Out",
  stock_take_increase: "Stock Take +",
  stock_take_decrease: "Stock Take −",
  manual_add: "Manual Add",
  manual_subtract: "Manual Subtract",
  damaged: "Damaged",
  theft_loss: "Theft / Loss",
  sample_gift: "Sample / Gift",
  correction_increase: "Correction +",
  correction_decrease: "Correction −",
  order_placed: "Order Placed",
  order_fulfilled: "Order Fulfilled",
  order_cancelled: "Order Cancelled",
  pi_reversal: "PI Reversed",
};

function StockHistoryPage() {
  const { showLoader, hideLoader } = useLoader();
  const [tableLoading, setTableLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(
    () => ({
      wareHouseId: searchParams.get("wareHouseId") || "",
      reason: searchParams.get("reason") || "",
      datefrom: searchParams.get("datefrom") || "",
      dateto: searchParams.get("dateto") || "",
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
    }),
    [searchParams],
  );

  const fetchMovementData = async () => {
    setTableLoading(true);

    try {
      const res = await inventoryAPI.getMovements(query);

      if (res.code === 200) {
        const { movements, pagination } = res.data;
        setItems(movements);
        setPagination(pagination);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchMovementData();
  }, [query]);

  const updateFilter = useCallback(
    (key, value) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      });
    },
    [setSearchParams],
  );

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock History</h4>
              <h6>Complete audit trail of all inventory movements</h6>
            </div>
          </div>
        </div>

        <div className="welcome align-items-center justify-content-between">
          <div className="row">
            <div className="col-md-3">
              <label className="form-label small">Location</label>
              <LocationSelect
                value={searchParams.get("wareHouseId")}
                onChange={(e) => updateFilter("wareHouseId", e.target.value)}
                showAll
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Reason</label>
              <select
                className="form-select"
                value={searchParams.get("reason")}
                onChange={(e) => updateFilter("reason", e.target.value)}
              >
                <option value="">All reasons</option>
                {Object.entries(REASON_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small">From Date</label>
              <input
                type="date"
                className="form-control"
                value={searchParams.get("datefrom")}
                onChange={(e) => updateFilter("datefrom", e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small">To Date</label>
              <input
                type="date"
                className="form-control"
                value={searchParams.get("dateto")}
                onChange={(e) => updateFilter("dateto", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4>Stock History Movements</h4>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Date/Time</th>
                    <th>SKU</th>
                    <th>WareHouse</th>
                    <th>Change</th>
                    <th>Reason</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={7} />
                  ) : items.length > 0 ? (
                    items.map((item, index) => (
                      <StockHistoryRow
                        key={item._id}
                        index={index}
                        item={item}
                        reasonLabels={REASON_LABELS}
                        page={query.page}
                        limit={query.limit}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted p-4">
                        No movement found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default StockHistoryPage;
